import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { AxiosResponse } from "axios";
import { TextField } from "@cmsgov/ds-medicare-gov";
import H1 from "../../common/components/h1";
import Div from "../../common/components/div";
import { SubmitButton } from "../buttons/Buttons";
import { MfaHelpDrawer } from "../shared-components/MfaHelpDrawer";
import { MfaEnrollStep1Response, useMfaApi } from "../hooks/useMfaApi";
import { validateEmail } from "../validators/mfaSchema";
import { SmartErrorAlert } from "../../common/components/SmartErrorAlert";
import { useMfaInlineError } from "../../common/hooks/use.analytics";
import { VerifyPassword } from "../VerifyPassword";
import translate from "../../common/functions/translate";
import capitalizeFirstLetter from "../../common/functions/capitalizeFirstLetter";
import { getMfaDuplicateChannelError } from "../../common/functions/getApiErrorMessage";

interface EnterEmailProps {
    type?: 'email'
    flow: 'add' | 'enroll'
}

export const EnterEmail = ({ type = 'email', flow }: EnterEmailProps) => {
    const [fieldValue, setFieldValue] = useState('');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [emailRef, setEmailRef] = useState<HTMLElement | null>(null); // removing this line breaks tests, unclear why
    const [fieldErrorText, setFieldErrorText] = useState('');
    const [showFieldError, setShowFieldError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState<AxiosResponse<MfaEnrollStep1Response> | null>(null);
    const [isVerified, setIsVerified] = useState(true);

    const history = useHistory();
    const api = useMfaApi();

    const inputLabel = translate('email_address');
    const inlineErr = useMfaInlineError(inputLabel);

    useEffect(() => {
        const errorKeys = validateEmail(fieldValue);
        setFieldErrorText(errorKeys ? translate(errorKeys[0]) : '');
    }, [fieldValue]);

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event?.preventDefault();
        setShowFieldError(true);
        setIsVerified(true);
        if (fieldErrorText || isLoading) {
            if (fieldErrorText) {
                inlineErr.validationError(fieldErrorText);
            }
            return;
        }
        setIsLoading(true);
        setApiResponse(null);
        const mfaStepResponse = await api.mfaEnrollStep1({
            type: type,
            value: fieldValue
        });
        const code = mfaStepResponse?.data.code;
        setApiResponse(mfaStepResponse);
        setIsLoading(false);
        if (code === 401) {
            setIsVerified(false);
            return;
        }
        const errors = mfaStepResponse?.data.errors || [];
        // set inline error for duplicate email if applicable
        const duplicateEmailErr = getMfaDuplicateChannelError(errors, 'email');
        setFieldErrorText(duplicateEmailErr);
        if (duplicateEmailErr) {
            inlineErr.serverError(duplicateEmailErr, 'MFA_DUPLICATE_CHANNEL');
        }
        if (code === 201) {
            history.push('/security-code/enroll/confirm-your-method', {
                type: type,
                value: fieldValue,
                flow
            });
        }
    }

    function updateFieldValue(value: string) {
        setFieldValue(value);
        if (showFieldError) {
            setShowFieldError(false);
        }
    }

    const buttonText = translate(`send_my_code_${type}`);
    const errorFlow = `MfaEnrollStep1${capitalizeFirstLetter(type)}` as const;
    if (!isVerified) {
        return <VerifyPassword type={type} flow={flow} onVerified={onSubmit} />;
    }

    const headingTitle = translate("enter_email_to_your_security_code");
    return (
        <form onSubmit={onSubmit} method="POST">
            <Div isRow>
                <Div cols={12} colsMD={8}>
                    <H1 className={'ds-u-margin-bottom--1'}>{translate(`mfa_title_${flow}`)}</H1>
                    <MfaHelpDrawer />
                </Div>
            </Div>
            <SmartErrorAlert
                className="ds-u-margin-top--0 ds-u-margin-bottom--3"
                name={errorFlow}
                fieldRef={null}
                apiResponse={apiResponse}
                conversion={null}
                invalidFields={null}
                onInvalidField={() => {}}
                parentHeading={headingTitle}
                mfaFlow={flow}
                mfaIsAnotherCodeRequested={false}
            />
            <Div isRow>
                <Div cols={12} colsMD={8}>
                    <h2 className={"ds-text-heading--xl"}>{headingTitle}</h2>
                </Div>
            </Div>
            <Div isRow>
                <Div cols={12} colsMD={8}>
                    <TextField
                        labelClassName="ds-u-margin-top--1"
                        className="ds-u-margin-bottom--3"
                        aria-invalid={!!(showFieldError && fieldErrorText)}
                        errorMessage={showFieldError ? fieldErrorText : null}
                        inputRef={setEmailRef}
                        label={inputLabel}
                        name={'emailAddress'}
                        onChange={event => updateFieldValue(event.currentTarget.value)}
                        onBlur={() => setShowFieldError(true)}
                        value={fieldValue}
                        hint={translate("hint_text")}
                    />
                </Div>
            </Div>
            <SubmitButton isLoading={isLoading} mfaAnalyticsData={{
                text: buttonText,
                parentComponentHeading: headingTitle
            }}>
                {buttonText}
            </SubmitButton>
        </form>
    );
}